import React, { createContext, useState, useEffect, useRef, useCallback } from 'react';

export const LoaderContext = createContext({
  isInitialLoad: true,
  setIsInitialLoad: (value: boolean) => {},
  addPendingResource: (id: string) => {},
  removePendingResource: (id: string) => {},
  isPageDataLoaded: (pageId: string) => {},
});

export const LoaderProvider = ({ children }: { children: React.ReactNode }) => {
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [pendingResources, setPendingResources] = useState<Set<string>>(new Set());
  const [loadedPages, setLoadedPages] = useState<Set<string>>(new Set());
  
  // Timing controls
  const startTime = useRef<number>(Date.now());
  const MIN_LOADER_DURATION = 1200; // Longer minimum to ensure smooth experience
  const CONTENT_SETTLE_DELAY = 400; // Time for content to visually settle
  
  // State refs for tracking completion
  const hasWindowLoaded = useRef(false);
  const hasContentSettled = useRef(false);
  const currentPageId = useRef<string>('');

  // --- Utilities ---
  const resizeTimer = useRef<number | null>(null);

  /**
   * Sync real viewport height to a CSS variable – crucial for mobile
   * browsers where 100vh is unreliable.
   */
  const setVh = () => {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);

    if (window.innerWidth <= 768) {
      document.body.classList.add('is-mobile');
    } else {
      document.body.classList.remove('is-mobile');
    }
  };

  /**
   * Force eager loading of priority images
   */
  const loadImagesWithPriority = () => {
    const profilePic = document.querySelector<HTMLImageElement>('.profile-pic');
    if (profilePic) {
      profilePic.loading = 'eager';
      // @ts-ignore – experimental property
      profilePic.fetchPriority = 'high';
    }
  };

  /**
   * Comprehensive content readiness check
   */
  const waitForContentReadiness = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      // Use multiple RAF to ensure DOM is fully rendered
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Check for images
            const images = Array.from(document.querySelectorAll('img'));
            let imagePromises: Promise<void>[] = [];

            if (images.length > 0) {
              imagePromises = images.map(img => {
                return new Promise<void>((imgResolve) => {
                  if (img.complete && img.naturalHeight !== 0) {
                    imgResolve();
                  } else {
                    const timeout = setTimeout(() => imgResolve(), 3000); // 3s timeout
                    img.addEventListener('load', () => {
                      clearTimeout(timeout);
                      imgResolve();
                    }, { once: true });
                    img.addEventListener('error', () => {
                      clearTimeout(timeout);
                      imgResolve();
                    }, { once: true });
                  }
                });
              });
            }

            // Wait for fonts
            const fontPromise = 'fonts' in document 
              ? Promise.race([
                  document.fonts.ready,
                  new Promise(resolve => setTimeout(resolve, 2000))
                ])
              : Promise.resolve();

            // Wait for all conditions
            Promise.all([...imagePromises, fontPromise]).then(() => {
              // Additional settle time for visual stability
              setTimeout(() => {
                hasContentSettled.current = true;
                resolve();
              }, CONTENT_SETTLE_DELAY);
            });
          });
        });
      });
    });
  }, []);

  /**
   * Add a pending resource that should complete before hiding loader
   */
  const addPendingResource = useCallback((id: string) => {
    setPendingResources(prev => new Set([...prev, id]));
  }, []);

  /**
   * Remove a pending resource
   */
  const removePendingResource = useCallback((id: string) => {
    setPendingResources(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  /**
   * Mark a page as having its data loaded
   */
  const isPageDataLoaded = useCallback((pageId: string) => {
    currentPageId.current = pageId;
    setLoadedPages(prev => new Set([...prev, pageId]));
  }, []);

  /**
   * Check if we can hide the loader
   */
  const checkAndHideLoader = useCallback(async () => {
    // Must have window load and current page data
    if (!hasWindowLoaded.current || !loadedPages.has(currentPageId.current)) {
      return;
    }

    // Must have no pending resources
    if (pendingResources.size > 0) {
      return;
    }

    // Wait for content to be visually ready
    await waitForContentReadiness();

    // Ensure minimum duration
    const elapsed = Date.now() - startTime.current;
    const remainingTime = Math.max(0, MIN_LOADER_DURATION - elapsed);

    setTimeout(() => {
      setIsInitialLoad(false);
    }, remainingTime);
  }, [loadedPages, pendingResources.size, waitForContentReadiness]);

  // Track route changes to reset state appropriately
  useEffect(() => {
    const handleRouteChange = () => {
      const currentPath = window.location.pathname;
      currentPageId.current = currentPath;
      hasContentSettled.current = false;
      
      // Only reset loader for full page changes, not initial load
      if (loadedPages.size > 0) {
        setIsInitialLoad(true);
        setLoadedPages(new Set());
        startTime.current = Date.now();
      }
    };

    // Listen for navigation events
    window.addEventListener('popstate', handleRouteChange);
    
    // Set initial page
    currentPageId.current = window.location.pathname;

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, [loadedPages.size]);

  // Initial setup
  useEffect(() => {
    setVh();
    loadImagesWithPriority();

    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker
          .register('/service-worker.js')
          .catch(err =>
            console.log('Service worker registration failed:', err)
          );
      });
    }

    const handleResize = () => {
      if (resizeTimer.current) {
        window.clearTimeout(resizeTimer.current);
      }
      resizeTimer.current = window.setTimeout(setVh, 100);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', () => setTimeout(setVh, 100));

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', () =>
        setTimeout(setVh, 100)
      );
    };
  }, []);

  // Wait for window load
  useEffect(() => {
    const handleWindowLoad = () => {
      hasWindowLoaded.current = true;
      checkAndHideLoader();
    };

    if (document.readyState === 'complete') {
      handleWindowLoad();
    } else {
      window.addEventListener('load', handleWindowLoad);
      return () => window.removeEventListener('load', handleWindowLoad);
    }
  }, [checkAndHideLoader]);

  // Re-check when dependencies change
  useEffect(() => {
    if (hasWindowLoaded.current) {
      checkAndHideLoader();
    }
  }, [checkAndHideLoader]);

  return (
    <LoaderContext.Provider 
      value={{ 
        isInitialLoad, 
        setIsInitialLoad,
        addPendingResource, 
        removePendingResource,
        isPageDataLoaded
      }}
    >
      {children}
    </LoaderContext.Provider>
  );
};